import { useEffect, useState } from 'react';

import { groupBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import { getAvatarByManufacturerName } from '@/utils';

import { AssetSelectionItem } from '@/components/pages/EmissionsDashboard';

import { useSearchReefersByIdQuery } from '@/graphql/SearchReefersById.generated';

import { AssetSearchHighlight } from './AssetSearchHighlight';

interface AssetSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectAsset: (asset: AssetSelectionItem) => void;
}

interface SearchResult {
  name: string;
  assetCount: number;
  refrigerationUnitModels: {
    name: string;
    assets: AssetSelectionItem[];
  }[];
}

// const searchRequestLimit = 100;
const maxAssetCountPerModel = 10;

function AssetSearchDialog(props: AssetSearchDialogProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { open, onClose, onSelectAsset } = props;
  const [search, setSearch] = useState('');

  const [searchResult, setSearchResult] = useState<SearchResult[] | null>(null);

  const handleSelectAsset = (reefer: AssetSelectionItem) => () =>
    onSelectAsset(reefer);

  const [{ data, fetching, error }] = useSearchReefersByIdQuery({
    pause: !open,
    variables: {
      search: `%${search}%`,
      // limit: searchRequestLimit,
    },
  });

  useEffect(() => {
    if (data) {
      // TODO: use webworker to do that
      // sort result by manufacturer & model
      const result = Object.entries(
        groupBy(
          data.container_insights_reefers,
          'refrigeration_unit_model.manufacturer.name',
        ),
      )
        .map(([manufacturerName, assets]) => ({
          name: manufacturerName,
          assetCount: assets.length,
          refrigerationUnitModels: Object.entries(
            groupBy(assets, 'refrigeration_unit_model.name'),
          )
            .map(([modelName, assets]) => ({
              name: modelName,
              assets,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setSearchResult(result);
    }
  }, [data]);

  const handleClearSearch = () => setSearch('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (/^[a-zA-Z0-9]*$/.test(event.target.value)) {
      setSearch(event.target.value);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={{ p: 2 }}
      // Cf: https://github.com/mui/material-ui/issues/33004
      disableRestoreFocus
    >
      <DialogTitle sx={{ pt: 3 }}>
        <TextField
          placeholder={t('searchAssetById')}
          id="search-asset-by-id"
          fullWidth
          autoFocus
          value={search}
          onChange={handleChange}
          helperText={error ? `An error occured while fetching assets` : null}
          error={Boolean(error)}
          slotProps={{
            input: {
              endAdornment: (
                <>
                  {fetching && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  )}
                  {search.length > 0 && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearSearch}>
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  )}
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                </>
              ),
            },
          }}
        />
      </DialogTitle>
      <DialogContent sx={{ pb: 0, minHeight: 520 }}>
        <Stack direction="column" spacing={2}>
          <Grid container spacing={1}>
            {searchResult?.map((manufacturer) => (
              <Grid key={manufacturer.name} size={{ xs: 6 }}>
                <Card sx={{ height: 250 }}>
                  <CardHeader
                    sx={{ pb: 1 }}
                    avatar={
                      <Avatar
                        variant="rounded"
                        slotProps={{ img: { sx: { objectFit: 'contain' } } }}
                        src={getAvatarByManufacturerName(manufacturer.name)}
                        sx={{
                          p: 0.5,
                          width: 38,
                          height: 38,
                        }}
                      />
                    }
                    title={
                      <Typography variant="subtitle2">
                        {manufacturer.name}
                      </Typography>
                    }
                    subheader={`${manufacturer.assetCount} assets`}
                  />
                  <CardContent
                    sx={{
                      pt: 1,
                      mr: 2,
                      ml: 1,
                      height: 160,
                      overflowY: 'auto',
                    }}
                  >
                    <Stack direction="column" spacing={1}>
                      {manufacturer.refrigerationUnitModels.map((model) => (
                        <Stack
                          key={model.name}
                          direction="column"
                          spacing={0.5}
                        >
                          <Stack
                            direction="column"
                            alignItems="flex-start"
                            spacing={0}
                          >
                            <Typography variant="caption">
                              {model.name}
                              <sup style={{ color: theme.palette.grey[600] }}>
                                {' '}
                                ({model.assets.length})
                              </sup>
                            </Typography>
                            <Divider sx={{ width: '100%' }} />
                          </Stack>
                          <Box sx={{ pl: 0 }}>
                            {model.assets
                              .slice(0, maxAssetCountPerModel)
                              .map((asset) => (
                                <Button
                                  key={asset.id}
                                  size="small"
                                  sx={{ mr: 1 }}
                                  onClick={handleSelectAsset(asset)}
                                >
                                  <AssetSearchHighlight
                                    text={asset.id}
                                    search={search}
                                    variant="button"
                                  />
                                </Button>
                              ))}
                            {model.assets.length > maxAssetCountPerModel && (
                              <Chip
                                size="small"
                                sx={{
                                  fontSize: theme.typography.pxToRem(10),
                                }}
                                label={`+${model.assets.length - maxAssetCountPerModel} more...`}
                              />
                            )}
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pl: 3.5, pr: 3.5 }}>
        {/* <Typography
          fontStyle={'italic'}
          sx={{ fontSize: theme.typography.pxToRem(12) }}
          flexGrow={1}
        >
          * Displayed results are limited to {searchRequestLimit} items.
        </Typography> */}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export { AssetSearchDialog };
